import React, { ReactElement, useEffect, useState } from 'react';
import { EditableUserInfo, StepperButtonsState } from '../../../../utils/interfaces';
import { ApiUrls } from '../../../../../api/api/apiUrls';
import { useRestOperations } from '../../../../common/rest/restOperations';
import { useTranslation } from '../../../../common/i18n/useTranslation';
import { errorNotification, successNotification } from '../../../../utils/notifications';
import { endpointConfig } from '../../../../configuration/configuration';
import { useFetchCoreData } from '../hooks/useFetchCoreData';
import { useLoadingContext } from '../../../contexts/LoadingContext';
import { useUpdateCachedItem } from '../../../../../api/cachedDataHooks';
import { validationUtils } from '../../../../utils/validationFunctions';
import { CompanyRegistartionStepEnum } from '../../../../utils/enums';
import { useCompanyRegistrationFileData } from '../steps/useCopmanyRegistration';
import { UserInfoDto, CompanyRegistrationDto, UserDto, AuthorizedSignatoryDto } from '../../../../api';
import { useRetryRequestContext } from '../../../contexts/RetryRequestContext';
import { useNavigate } from 'react-router-dom';

interface CompanyRegistrationContext {
    uploadCompanyIntroduction: () => Promise<void>;
    updateAdmin: (property: string, value: string) => void;
    refetchRegistration: (userOverride?: UserInfoDto) => Promise<void>;
    updateSignatory: (property: string, value: string) => void;
    registrationId: string;
    setRegistrationId: React.Dispatch<React.SetStateAction<string>>;
    updateButtonsDisableState: (index: number, disabled: boolean) => void;
    initButtonsInfo: (info: StepperButtonsState[]) => void;
    registration: CompanyRegistrationDto | undefined;
    setRegistration: React.Dispatch<React.SetStateAction<CompanyRegistrationDto | undefined>>;
    buttonsInfo: StepperButtonsState[];
    setOpenTermsOfUseDialog: React.Dispatch<React.SetStateAction<boolean>>;
    openTermsOfUseDialog: boolean;
    localCompanyIntroductionFile: File[];
    setLocalCompanyIntroductionFile: React.Dispatch<React.SetStateAction<File[]>>;
    downloadCompanyIntroductionFileHandler: (fileId: string, fileName: string) => Promise<void>;
    deleteFileDialogOpen: boolean;
    deleteFileClickedHandler: (fileId: string) => void;
    handleDeleteFile: () => void;
    changeDeleteDialogVisability: () => void;
    isUploadingIntroductionFile: boolean;
    updateCompanyRegistration: (property: string, value: string | boolean) => void;
    isFullBeam: () => boolean;
    isFreeBeam: () => boolean;
    updateAdminMultiple: (updates: Partial<UserDto>) => void;
    setIsUmasRegistrationModalOpen: React.Dispatch<React.SetStateAction<boolean>>;
    isUmasRegistrationModalOpen: boolean;
    //userInfo
    userInfo: UserInfoDto | undefined;
    setUserInfo: React.Dispatch<React.SetStateAction<UserInfoDto | undefined>>;
    isAdmin: boolean | undefined;
    loginTriggered: boolean | undefined;
    setLoginTriggered: React.Dispatch<React.SetStateAction<boolean | undefined>>;
    saveCompanyRegistration: (companyRegistration: CompanyRegistrationDto) => void;
    saveCompanyRegistrationUsers: (signatory: AuthorizedSignatoryDto) => void;
    saveUserInfo: (user: UserDto) => void;
    isCompanyRegistrationDataFetching: boolean;
    isAdminAndUser: boolean;
}

const initState: CompanyRegistrationContext = {
    uploadCompanyIntroduction: () => new Promise(() => {}),
    updateAdmin: () => {},
    updateSignatory: () => {},
    registrationId: '',
    setRegistrationId: () => {},
    updateButtonsDisableState: () => {},
    initButtonsInfo: () => {},
    registration: {} as CompanyRegistrationDto,
    setRegistration: () => {},
    buttonsInfo: [],
    refetchRegistration: () => new Promise(() => {}),
    setOpenTermsOfUseDialog: () => {},
    openTermsOfUseDialog: false,
    isFullBeam: () => false,
    isFreeBeam: () => false,
    updateAdminMultiple: () => {},
    localCompanyIntroductionFile: [],
    setLocalCompanyIntroductionFile: () => {},
    downloadCompanyIntroductionFileHandler: () => new Promise(() => {}),
    deleteFileDialogOpen: false,
    deleteFileClickedHandler: () => {},
    handleDeleteFile: () => {},
    changeDeleteDialogVisability: () => {},
    isUploadingIntroductionFile: false,
    updateCompanyRegistration: () => {},
    setIsUmasRegistrationModalOpen: () => {},
    isUmasRegistrationModalOpen: false,
    //userInfo
    userInfo: {} as UserInfoDto,
    setUserInfo: () => {},
    isAdmin: {} as boolean,
    loginTriggered: {} as boolean,
    setLoginTriggered: () => {},
    saveCompanyRegistration: () => {},
    saveCompanyRegistrationUsers: () => {},
    saveUserInfo: () => {},
    isCompanyRegistrationDataFetching: false,
    isAdminAndUser: false,
};

const companyRegistrationContext = React.createContext<CompanyRegistrationContext>(initState);

export function ClientCompanyRegistrationContextProvider(props: Readonly<{ children: React.ReactNode }>): ReactElement {
    const [localCompanyIntroductionFile, setLocalCompanyIntroductionFile] = useState<File[]>([]);
    const [registrationId, setRegistrationId] = useState<string>('');
    const translate = useTranslation();
    const [registration, setRegistration] = useState<CompanyRegistrationDto>();
    const [buttonsInfo, setButtonsInfo] = useState<StepperButtonsState[]>([]);
    const { doDownloadFile, doUploadFile } = useRestOperations<CompanyRegistrationDto>();
    const [openTermsOfUseDialog, setOpenTermsOfUseDialog] = useState<boolean>(false);
    const { setOverwriteDialogMessagesAndOptions, openOverwriteDialog, configureNextRequest } =
        useRetryRequestContext();

    const [deleteCompanyIntroductionFileId, setDeleteCompanyIntroductionFileId] = useState<string>('');
    const [deleteFileDialogOpen, setDeleteFileDialogOpen] = useState<boolean>(false);
    const [isUploadingIntroductionFile, setIsUploadingIntroductionFile] = useState<boolean>(false);
    const [isUmasRegistrationModalOpen, setIsUmasRegistrationModalOpen] = useState<boolean>(false);
    const {
        refetchCompanyRegistrationData,
        refetchUserInfoData,
        userData,
        updateUserInfoMutation,
        isCompanyRegistrationDataFetching,
    } = useFetchCoreData();
    const { refetchCompanyRegistrationFileData } = useCompanyRegistrationFileData(registrationId, true);

    //userInfo
    const [userInfo, setUserInfo] = useState<UserInfoDto>();
    const [loginTriggered, setLoginTriggered] = useState<boolean>();
    const [isAdmin, setIsAdmin] = useState<boolean>();
    const [isAdminAndUser, setIsAdminAndUser] = useState<boolean>(false);
    const { setLoading, startLoading, stopLoading } = useLoadingContext();

    const navigate = useNavigate();

    const deleteCompanyIntroductionFileMutation = useUpdateCachedItem<string>({
        url: ApiUrls.userApi.deleteFileByIdForRegistrationById(registrationId, deleteCompanyIntroductionFileId),
        method: 'DELETE',
        queryKey: 'FILES',
    });

    const updateCompanyRegistrationMutation = useUpdateCachedItem<CompanyRegistrationDto>({
        url: ApiUrls.userApi.registrationById(registration?.id ?? ''),
        method: 'PUT',
        queryKey: 'COMPANY-REGISTRATION',
        conflictResolutionOptions: {
            onConflict: {
                refetch: async () => {
                    await loadCompanyRegistrationData();
                },
            },
        },
    });

    const saveCompanyRegistration = (companyRegistration: CompanyRegistrationDto) => {
        updateCompanyRegistrationMutation.mutate(companyRegistration, {
            onSuccess: async (data) => {
                if (data) {
                    successNotification(translate('notification_successfully_data_changed_company_registration'));
                    await loadCompanyRegistrationData();
                }
            },
        });
    };

    const saveCompanyRegistrationUsers = (signatory: AuthorizedSignatoryDto) => {
        if (!registration) {
            return;
        }

        const updatedRegistration = {
            ...registration,
            authorizedSignatory: signatory,
        };

        updateCompanyRegistrationMutation.mutate(updatedRegistration, {
            onSuccess: async (data) => {
                if (data) {
                    successNotification(translate('notification_successfully_data_changed_company_registration'));
                    await loadCompanyRegistrationData();
                }
            },
        });
    };

    const changeDeleteDialogVisability = () => {
        setDeleteFileDialogOpen((prevState) => !prevState);
    };

    const deleteFileClickedHandler = (fileId: string) => {
        setDeleteCompanyIntroductionFileId(fileId);
        changeDeleteDialogVisability();
    };
    const navigateToMain = async () => {
        await navigate('/');
    };
    const handleConflictError = () => {
        configureNextRequest({
            onConflict: {
                refetch: async () => {
                    await navigateToMain();
                    await loadCompanyRegistrationData();
                },
                overwrite: false,
            },
        });
        setOverwriteDialogMessagesAndOptions({
            showSaveButton: false,
            dialogContentMessage: translate('notification_registration_has_been_sent'),
            dialogTitle: translate('notification_registration_registration_closed'),
        });

        openOverwriteDialog();
    };
    const handleDeleteFile = () => {
        if (
            !validationUtils.isStringEmpty(deleteCompanyIntroductionFileId) &&
            !validationUtils.isStringEmpty(registrationId)
        ) {
            deleteCompanyIntroductionFileMutation.mutate(deleteCompanyIntroductionFileId, {
                onSuccess: () => {
                    successNotification(translate('notification_successfully_deleted_file'));
                    updateButtonsDisableState(CompanyRegistartionStepEnum.UPLOAD_COMPANY_PRESENTATION, true);
                },
                onError: (error) => {
                    if (error instanceof Response) {
                        if (error.status === 400) {
                            handleConflictError();
                        }
                    }
                },
            });
            changeDeleteDialogVisability();
        }
    };

    const downloadCompanyIntroductionFileHandler = async (fileId: string, fileName: string) => {
        if (!validationUtils.isStringEmpty(fileId) && registrationId) {
            await doDownloadFile(ApiUrls.userApi.downloadFileByIdForRegistrationById(registrationId, fileId), fileName);
        }
    };

    // This useEffect is triggered only when the page is reloaded
    useEffect(() => {
        if (!userData) {
            loadUserInfo().catch(() => {
                console.error('Error loading user info');
            });
        }
    }, [userData]);

    // This useEffect is triggered when the loginTriggered true
    useEffect(() => {
        if (loginTriggered) {
            setLoginTriggered(false);
            loadUserInfo().catch(() => {
                console.error('Error loading user info:');
            });
        }
    }, [loginTriggered]);

    const loadCompanyRegistrationData = async (userOverride?: UserInfoDto) => {
        try {
            const user = userOverride ?? userData;
            if (!user) {
                return;
            }

            const result = await refetchCompanyRegistrationData();

            if (result && Array.isArray(result.data) && result.data.length > 0) {
                const companiesResponse = result.data[0];

                setRegistration({
                    ...companiesResponse,
                    admin: {
                        email: user.email,
                        firstName: user.givenName,
                        lastName: user.familyName,
                        phoneNumber: user.phoneNumber,
                        salutation: user.salutation,
                    },
                });
            } else {
                console.warn('No valid company data found');
            }
        } catch (error) {
            console.error('Error fetching company registration data', error);
            errorNotification(translate('error_loading_registration'));
        }
    };

    const loadUserInfo = async () => {
        try {
            startLoading();
            const result = await refetchUserInfoData();
            if (!result?.data) {
                stopLoading();
                setLoading(false);
                return;
            }
            const user = result.data as UserInfoDto;
            setUserInfo(user);
            const isJustAdmin = user.entitlementGroup?.includes(`${endpointConfig.env.ROLE_PREFIX}.BEAM_ADMIN`);
            const isAdminAndUser =
                user.entitlementGroup?.includes(`${endpointConfig.env.ROLE_PREFIX}.BEAM_USER`) && isJustAdmin;
            setIsAdmin(isJustAdmin ?? false);
            setIsAdminAndUser(isAdminAndUser ?? false);
            await loadCompanyRegistrationData(user);
        } catch {
            stopLoading();
            setLoading(false);
        } finally {
            stopLoading();
            setLoading(false);
        }
    };
    const saveUserInfo = (user: UserDto) => {
        const putData: EditableUserInfo = {
            salutation: user.salutation ?? UserDto.salutation.OTHER,
            phoneNumber: user.phoneNumber ?? '',
        };
        updateUserInfoMutation.mutate(putData, {
            onSuccess: async (putData) => {
                if (putData) {
                    setUserInfo((props) => ({
                        ...props,
                        salutation: putData.salutation,
                        phoneNumber: putData.phoneNumber,
                    }));
                }
            },
        });
    };

    const uploadCompanyIntroduction = async () => {
        setIsUploadingIntroductionFile(true);
        try {
            await doUploadFile({
                apiUrl: ApiUrls.userApi.registrationFileById(registrationId),
                files: localCompanyIntroductionFile,
            });
        } finally {
            setLocalCompanyIntroductionFile([]);
        }
        await refetchCompanyRegistrationFileData();
        setIsUploadingIntroductionFile(false);
    };

    const updateAdmin = (property: string, value: string) => {
        const updatedAdmin = {
            ...(registration?.admin ?? ({} as UserDto)),
            [property]: value,
        };
        setRegistration({ ...registration, admin: updatedAdmin });
    };

    const updateAdminMultiple = (updates: Partial<UserDto>) => {
        const updatedAdmin = {
            ...(registration?.admin ?? ({} as UserDto)),
            ...updates,
        };
        setRegistration({ ...registration, admin: updatedAdmin });
    };

    const updateSignatory = (property: string, value: string) => {
        const updatedASignatory: AuthorizedSignatoryDto = {
            ...(registration?.authorizedSignatory ?? ({} as AuthorizedSignatoryDto)),
            [property]: value,
        };
        setRegistration({ ...registration, authorizedSignatory: updatedASignatory });
    };

    const updateCompanyRegistration = (property: string, value: string | boolean) => {
        const updatedCompanyRegistration: CompanyRegistrationDto = {
            ...registration,
            [property]: value,
        };
        setRegistration(updatedCompanyRegistration);
    };

    const updateButtonsDisableState = (index: number, disabled: boolean) => {
        const buttonsInfoButtonsButton = buttonsInfo.map((info) => {
            if (info.idx === index) {
                return { ...info, disabled: disabled };
            } else {
                return info;
            }
        });
        setButtonsInfo(buttonsInfoButtonsButton);
    };

    const initButtonsInfo = (info: StepperButtonsState[]) => {
        setButtonsInfo(info);
    };

    const isFullBeam = () => {
        return registration?.usageMode === CompanyRegistrationDto.usageMode.BEAM_FULL;
    };

    const isFreeBeam = () => {
        return registration?.usageMode === CompanyRegistrationDto.usageMode.BEAM_FREE;
    };

    return (
        <companyRegistrationContext.Provider
            value={{
                uploadCompanyIntroduction,
                updateAdmin,
                updateSignatory,
                registrationId,
                setRegistrationId,
                initButtonsInfo: initButtonsInfo,
                updateButtonsDisableState: updateButtonsDisableState,
                buttonsInfo: buttonsInfo,
                registration: registration,
                setRegistration: setRegistration,
                refetchRegistration: loadCompanyRegistrationData,
                setOpenTermsOfUseDialog,
                openTermsOfUseDialog,
                isFullBeam,
                isFreeBeam,
                updateAdminMultiple,
                localCompanyIntroductionFile,
                setLocalCompanyIntroductionFile,
                downloadCompanyIntroductionFileHandler,
                deleteFileDialogOpen,
                deleteFileClickedHandler,
                handleDeleteFile,
                changeDeleteDialogVisability,
                isUploadingIntroductionFile,
                updateCompanyRegistration,
                setIsUmasRegistrationModalOpen,
                isUmasRegistrationModalOpen,
                isCompanyRegistrationDataFetching,
                userInfo,
                setUserInfo,
                isAdmin: isAdmin,
                loginTriggered: loginTriggered,
                setLoginTriggered: setLoginTriggered,
                saveCompanyRegistration,
                saveCompanyRegistrationUsers,
                saveUserInfo,
                isAdminAndUser,
            }}
        >
            {props.children}
        </companyRegistrationContext.Provider>
    );
}

export function useClientCompanyRegistrationContext() {
    return React.useContext(companyRegistrationContext);
}
